/**
 * ALDER SEARCH — Insights Section (Homepage)
 * Shows top 4 articles from the shared insights data.
 * Cards link to individual article pages.
 * "View all insights" links to /insights hub.
 */

import { useEffect, useRef } from "react";
import { Link } from "wouter";
import { insights } from "@/data/insights";
import { media } from "@/data/media";

export default function InsightsSection() {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const allEls = [headerRef.current, ...cardsRef.current];

    if (prefersReduced) {
      // Show all content immediately for reduced-motion users
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );

    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 100}ms, transform 0.7s ease-out ${i * 100}ms`;
      observer.observe(el);
    });

    // Fallback: ensure all elements become visible after 2s regardless
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  const featured = insights.find((article) => article.slug === "the-cpo-as-value-creation-partner") ?? insights[0];
  const rest = insights.filter((article) => article.slug !== featured.slug).slice(0, 3);

  return (
    <section
      id="insights"
      className="py-28"
      style={{ background: "oklch(0.978 0.006 80)" }}
    >
      <div className="container">
        {/* Section Header */}
        <div ref={headerRef} className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div
                className="h-px w-10"
                style={{ background: "oklch(0.62 0.085 75)" }}
              />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Perspectives
              </span>
            </div>
            <h2
              className="font-display"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.245 0.065 155)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Insights on VC &amp; PE Leadership
            </h2>
          </div>
          <Link
            href="/insights"
            className="font-ui text-sm self-start lg:self-end"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 500,
              fontSize: "0.85rem",
              color: "oklch(0.245 0.065 155)",
              textDecoration: "none",
              padding: "4px 0",
              letterSpacing: "0.02em",
              borderBottom: "1px solid oklch(0.245 0.065 155 / 0.3)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = "oklch(0.62 0.085 75)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)";
              (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = "oklch(0.245 0.065 155 / 0.3)";
            }}
          >
            View all {insights.length} insights &rarr;
          </Link>
        </div>

        {/* Featured + Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Featured Article */}
          <Link
            href={`/insights/${featured.slug}`}
            className="lg:col-span-3 block"
            style={{ textDecoration: "none" }}
          >
            <div
              ref={(el) => { cardsRef.current[0] = el; }}
              className="group overflow-hidden h-full"
              style={{
                background: "white",
                border: "1px solid oklch(0.88 0.018 80)",
                borderRadius: "4px",
                transition: "box-shadow 0.25s ease, border-color 0.25s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 32px oklch(0.28 0.07 155 / 0.1)";
                (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.28 0.07 155 / 0.3)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.88 0.018 80)";
              }}
            >
              {/* Image */}
              <div className="relative overflow-hidden" style={{ height: "260px" }}>
                <picture>
                  <source type="image/avif" srcSet={media.portfolio.avif} />
                  <img
                    src={media.portfolio.webp}
                    srcSet={media.portfolio.srcSet}
                    sizes="(min-width: 1024px) 60vw, 100vw"
                    alt="Featured insight"
                    width={media.portfolio.width}
                    height={media.portfolio.height}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </picture>
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to top, rgba(27,58,45,0.7) 0%, transparent 60%)",
                  }}
                />
                <div className="absolute top-4 left-4">
                  <span
                    className="font-ui text-xs px-3 py-1.5"
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.65rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      background: "oklch(0.62 0.085 75)",
                      color: "oklch(0.18 0.055 155)",
                      borderRadius: "2px",
                    }}
                  >
                    {featured.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <div
                  className="flex items-center gap-3 mb-4"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 300,
                    fontSize: "0.7rem",
                    color: "oklch(0.55 0.015 155)",
                    letterSpacing: "0.06em",
                  }}
                >
                  <span>{featured.date}</span>
                </div>
                <h3
                  className="font-display mb-3 group-hover:opacity-80 transition-opacity"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 700,
                    fontSize: "1.4rem",
                    color: "oklch(0.245 0.065 155)",
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {featured.title}
                </h3>
                <p
                  className="font-body mb-4"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 400,
                    fontSize: "0.9rem",
                    lineHeight: 1.8,
                    color: "oklch(0.45 0.015 155)",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {featured.excerpt}
                </p>
                <span
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: "0.75rem",
                    color: "oklch(0.38 0.07 155)",
                    letterSpacing: "0.06em",
                  }}
                >
                  Read article →
                </span>
              </div>
            </div>
          </Link>

          {/* Secondary Articles */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {rest.map((article, i) => (
              <Link
                key={article.slug}
                href={`/insights/${article.slug}`}
                className="block h-full"
                style={{ textDecoration: "none" }}
              >
                <div
                  ref={(el) => { cardsRef.current[i + 1] = el; }}
                  className="group p-6 flex-1 h-full"
                  style={{
                    background: "white",
                    border: "1px solid oklch(0.88 0.018 80)",
                    borderRadius: "4px",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderLeftWidth = "3px";
                    (e.currentTarget as HTMLDivElement).style.borderLeftColor = "oklch(0.62 0.085 75)";
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px oklch(0.28 0.07 155 / 0.07)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderLeftWidth = "1px";
                    (e.currentTarget as HTMLDivElement).style.borderLeftColor = "oklch(0.88 0.018 80)";
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      style={{
                        fontFamily: "'DM Sans', system-ui, sans-serif",
                        fontWeight: 600,
                        fontSize: "0.62rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        background: "oklch(0.952 0.012 80)",
                        color: "oklch(0.32 0.07 155)",
                        borderRadius: "2px",
                        padding: "2px 8px",
                      }}
                    >
                      {article.category}
                    </span>
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 300,
                        fontSize: "0.65rem",
                        color: "oklch(0.55 0.015 155)",
                      }}
                    >
                      {article.date}
                    </span>
                  </div>
                  <h3
                    className="font-display mb-2 group-hover:opacity-75 transition-opacity"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "1rem",
                      color: "oklch(0.245 0.065 155)",
                      lineHeight: 1.35,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {article.title}
                  </h3>
                  <p
                    className="font-body"
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      lineHeight: 1.75,
                      color: "oklch(0.5 0.015 155)",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <Link
            href="/insights"
            className="inline-block px-8 py-3.5 text-xs font-semibold tracking-widest uppercase"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              border: "1px solid oklch(0.28 0.07 155)",
              color: "oklch(0.28 0.07 155)",
              borderRadius: "2px",
              textDecoration: "none",
              letterSpacing: "0.1em",
              transition: "background 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = "oklch(0.28 0.07 155)";
              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.92 0.01 80)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.28 0.07 155)";
            }}
          >
            View all {insights.length} insights
          </Link>
        </div>
      </div>
    </section>
  );
}
